<script setup>
import { h, ref, watch, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DurationInput from 'next/input/DurationInput.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';

const { t } = useI18n();

// Workflow Settings (AI Agent)
const duration = ref(0);
const unit = ref(DURATION_UNITS.MINUTES);
const message = ref('');
const labelToApply = ref({});
const ignoreWaiting = ref(false);
const isEnabled = ref(false);

// Standard Settings
const durationStandard = ref(0);
const unitStandard = ref(DURATION_UNITS.MINUTES);
const messageStandard = ref('');
const labelToApplyStandard = ref({});
const ignoreWaitingStandard = ref(false);
const isEnabledStandard = ref(false);
const selectedInboxesStandard = ref([]);

const isSubmitting = ref(false);

const { currentAccount, updateAccount } = useAccount();

const labels = useMapGetter('labels/getLabels');
const inboxes = useMapGetter('inboxes/getInboxes');

const inboxOptions = computed(() => {
  if (!inboxes.value?.length) return [];
  return inboxes.value.map(inbox => ({
    id: inbox.id,
    name: inbox.name,
    avatar_url: inbox.avatar_url,
  }));
});

const getInboxOption = inboxId => {
  return inboxOptions.value.find(option => option.id === inboxId) || null;
};

const selectedInboxesStandardModel = computed({
  get() {
    return (selectedInboxesStandard.value || [])
      .map(getInboxOption)
      .filter(Boolean);
  },
  set(val) {
    selectedInboxesStandard.value = val ? val.map(opt => opt.id || opt) : [];
  },
});

const labelOptions = computed(() =>
  labels.value?.length
    ? labels.value.map(label => ({
        id: label.title,
        name: label.title,
        icon: h('span', {
          class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
          style: { backgroundColor: label.color },
        }),
      }))
    : []
);

const getLabelOption = labelName => {
  return labelOptions.value.find(option => option.name === labelName) || null;
};

const getUnitFromDuration = dur => {
  if (!dur) return DURATION_UNITS.MINUTES;
  if (dur % (24 * 60) === 0) return DURATION_UNITS.DAYS;
  if (dur % 60 === 0) return DURATION_UNITS.HOURS;
  return DURATION_UNITS.MINUTES;
};

watch(
  [currentAccount, labelOptions],
  () => {
    const {
      auto_resolve_after,
      auto_resolve_message,
      auto_resolve_ignore_waiting,
      auto_resolve_label,
      auto_resolve_after_standard,
      auto_resolve_message_standard,
      auto_resolve_ignore_waiting_standard,
      auto_resolve_label_standard,
      auto_resolve_inbox_ids_standard,
    } = currentAccount.value?.settings || {};

    // Workflow
    duration.value = auto_resolve_after;
    message.value = auto_resolve_message;
    ignoreWaiting.value = auto_resolve_ignore_waiting;
    labelToApply.value = getLabelOption(auto_resolve_label);
    unit.value = getUnitFromDuration(duration.value);
    isEnabled.value = !!duration.value;

    // Standard
    durationStandard.value = auto_resolve_after_standard;
    messageStandard.value = auto_resolve_message_standard;
    ignoreWaitingStandard.value = auto_resolve_ignore_waiting_standard;
    labelToApplyStandard.value = getLabelOption(auto_resolve_label_standard);
    selectedInboxesStandard.value = auto_resolve_inbox_ids_standard || [];
    unitStandard.value = getUnitFromDuration(durationStandard.value);
    isEnabledStandard.value = !!durationStandard.value;
  },
  { deep: true, immediate: true }
);

const updateAccountSettings = async settings => {
  try {
    isSubmitting.value = true;
    await updateAccount(settings, { silent: true });
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  const settings = {};

  if (isEnabled.value) {
    if (duration.value < 1) {
      useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.ERROR'));
      return;
    }
    settings.auto_resolve_after = duration.value;
    settings.auto_resolve_message = message.value;
    settings.auto_resolve_ignore_waiting = ignoreWaiting.value;
    settings.auto_resolve_label = labelToApply.value?.name || null;
  } else {
    settings.auto_resolve_after = null;
    settings.auto_resolve_message = '';
    settings.auto_resolve_ignore_waiting = false;
    settings.auto_resolve_label = null;
  }

  if (isEnabledStandard.value) {
    if (durationStandard.value < 1) {
      useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.ERROR'));
      return;
    }
    settings.auto_resolve_after_standard = durationStandard.value;
    settings.auto_resolve_message_standard = messageStandard.value;
    settings.auto_resolve_ignore_waiting_standard = ignoreWaitingStandard.value;
    settings.auto_resolve_label_standard =
      labelToApplyStandard.value?.name || null;
    settings.auto_resolve_inbox_ids_standard = selectedInboxesStandard.value
      ?.length
      ? selectedInboxesStandard.value
      : null;
  } else {
    settings.auto_resolve_after_standard = null;
    settings.auto_resolve_message_standard = '';
    settings.auto_resolve_ignore_waiting_standard = false;
    settings.auto_resolve_label_standard = null;
    settings.auto_resolve_inbox_ids_standard = null;
  }

  await updateAccountSettings(settings);
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.NOTE')"
    with-border
  >
    <form class="grid gap-8" @submit.prevent="handleSubmit">
      <!-- AI AGENT SECTION -->
      <div class="grid gap-5 p-4 border border-n-weak rounded-2xl bg-n-solid-1">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.WORKFLOW_TITLE') }}
          </h3>
          <Switch v-model="isEnabled" />
        </div>

        <div v-if="isEnabled" class="grid gap-5">
          <WithLabel
            :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.LABEL')"
            :help-message="
              t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.HELP')
            "
          >
            <div class="gap-2 w-full grid grid-cols-[3fr_1fr]">
              <DurationInput
                v-model="duration"
                v-model:unit="unit"
                min="0"
                max="1438560"
                class="w-full"
              />
            </div>
          </WithLabel>
          <WithLabel
            :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.LABEL')"
            :help-message="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.HELP')"
          >
            <TextArea
              v-model="message"
              class="w-full"
              :placeholder="
                t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.PLACEHOLDER')
              "
            />
          </WithLabel>
          <WithLabel
            :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.PREFERENCES')"
          >
            <div
              class="rounded-xl border border-n-weak bg-n-solid-2 w-full text-sm text-n-slate-12 divide-y divide-n-weak"
            >
              <div class="p-3 h-12 flex items-center justify-between">
                <span>
                  {{
                    t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.IGNORE_WAITING.LABEL')
                  }}
                </span>
                <Switch v-model="ignoreWaiting" />
              </div>
              <div class="p-3 h-12 flex items-center justify-between">
                <span>
                  {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.LABEL') }}
                </span>
                <SingleSelect
                  v-model="labelToApply"
                  :options="labelOptions"
                  :placeholder="
                    $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.PLACEHOLDER')
                  "
                  placeholder-icon="i-lucide-chevron-down"
                  placeholder-trailing-icon
                  variant="faded"
                />
              </div>
            </div>
          </WithLabel>
        </div>
      </div>

      <!-- STANDARD SECTION -->
      <div class="grid gap-5 p-4 border border-n-weak rounded-2xl bg-n-solid-1">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.STANDARD_TITLE') }}
          </h3>
          <Switch v-model="isEnabledStandard" />
        </div>

        <div v-if="isEnabledStandard" class="grid gap-5">
          <WithLabel
            label="Inboxes"
            help-message="Select the inboxes that this auto-resolve strategy applies to. Leave empty to apply to all inboxes."
          >
            <MultiSelect
              v-model="selectedInboxesStandardModel"
              :options="inboxOptions"
              class="w-full"
            />
          </WithLabel>
          <WithLabel
            :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.LABEL')"
            :help-message="
              t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.HELP')
            "
          >
            <div class="gap-2 w-full grid grid-cols-[3fr_1fr]">
              <DurationInput
                v-model="durationStandard"
                v-model:unit="unitStandard"
                min="0"
                max="1438560"
                class="w-full"
              />
            </div>
          </WithLabel>
          <WithLabel
            :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.LABEL')"
            :help-message="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.HELP')"
          >
            <TextArea
              v-model="messageStandard"
              class="w-full"
              :placeholder="
                t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.PLACEHOLDER')
              "
            />
          </WithLabel>
          <WithLabel
            :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.PREFERENCES')"
          >
            <div
              class="rounded-xl border border-n-weak bg-n-solid-2 w-full text-sm text-n-slate-12 divide-y divide-n-weak"
            >
              <div class="p-3 h-12 flex items-center justify-between">
                <span>
                  {{
                    t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.IGNORE_WAITING.LABEL')
                  }}
                </span>
                <Switch v-model="ignoreWaitingStandard" />
              </div>
              <div class="p-3 h-12 flex items-center justify-between">
                <span>
                  {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.LABEL') }}
                </span>
                <SingleSelect
                  v-model="labelToApplyStandard"
                  :options="labelOptions"
                  :placeholder="
                    $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.PLACEHOLDER')
                  "
                  placeholder-icon="i-lucide-chevron-down"
                  placeholder-trailing-icon
                  variant="faded"
                />
              </div>
            </div>
          </WithLabel>
        </div>
      </div>

      <div class="flex gap-2">
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.UPDATE_BUTTON')"
        />
      </div>
    </form>
  </SectionLayout>
</template>
